/**
 * VoiceTransactionSheet — Confirmation bottom sheet for voice-parsed transactions.
 * Shows pre-filled editable fields with confidence indicator.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  Slide,
} from '@mui/material';
import {
  Mic as MicIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Replay as ReplayIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
} from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function getConfidenceColor(confidence) {
  if (confidence >= 0.7) return 'success';
  if (confidence >= 0.4) return 'warning';
  return 'error';
}

function getConfidenceKey(confidence) {
  if (confidence >= 0.7) return 'voice.confidence.high';
  if (confidence >= 0.4) return 'voice.confidence.medium';
  return 'voice.confidence.low';
}

export default function VoiceTransactionSheet({
  open,
  onClose,
  parsedData,
  categories = [],
  onSave,
  onEditMore,
  onRetry,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    type: 'expense',
    amount: '',
    description: '',
    categoryId: '',
    date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (parsedData) {
      setFormData({
        type: parsedData.type || 'expense',
        amount: parsedData.amount || '',
        description: parsedData.description || '',
        categoryId: parsedData.categoryId || (categories[0]?._id || categories[0]?.id || ''),
        date: parsedData.date || new Date().toISOString().split('T')[0],
      });
    }
  }, [parsedData, categories]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = () => {
    const budgetMonth = formData.date ? formData.date.substring(0, 7) : undefined;
    onSave({
      ...formData,
      amount: parseFloat(formData.amount) || 0,
      budgetMonth,
    });
  };

  const handleEditMore = () => {
    const budgetMonth = formData.date ? formData.date.substring(0, 7) : undefined;
    onEditMore({
      ...formData,
      amount: parseFloat(formData.amount) || 0,
      budgetMonth,
    });
  };

  const confidence = parsedData?.confidence || 0;
  const isValid = formData.amount && parseFloat(formData.amount) > 0 && formData.description;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={isMobile}
      TransitionComponent={Transition}
      PaperProps={{
        sx: isMobile ? {
          position: 'fixed',
          bottom: 0,
          m: 0,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          maxHeight: '85vh',
        } : {},
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <MicIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {t('voice.parsedAs', 'Parsed as')}
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={t(getConfidenceKey(confidence), `${Math.round(confidence * 100)}%`)}
            color={getConfidenceColor(confidence)}
            variant="outlined"
          />
        </Stack>
        {parsedData?.rawText && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
            "{parsedData.rawText}"
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Type toggle */}
          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              variant={formData.type === 'expense' ? 'contained' : 'outlined'}
              color="error"
              startIcon={<ExpenseIcon />}
              onClick={() => setFormData((p) => ({ ...p, type: 'expense' }))}
              size="small"
            >
              Expense
            </Button>
            <Button
              fullWidth
              variant={formData.type === 'income' ? 'contained' : 'outlined'}
              color="success"
              startIcon={<IncomeIcon />}
              onClick={() => setFormData((p) => ({ ...p, type: 'income' }))}
              size="small"
            >
              Income
            </Button>
          </Stack>

          {/* Amount */}
          <TextField
            label={t('transactions.amount', 'Amount')}
            value={formData.amount}
            onChange={handleChange('amount')}
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            fullWidth
            size="small"
            autoFocus={!formData.amount}
          />

          {/* Description */}
          <TextField
            label={t('transactions.description', 'Description')}
            value={formData.description}
            onChange={handleChange('description')}
            fullWidth
            size="small"
          />

          {/* Category (expense only) */}
          {formData.type === 'expense' && categories.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>{t('transactions.category', 'Category')}</InputLabel>
              <Select
                value={formData.categoryId}
                onChange={handleChange('categoryId')}
                label={t('transactions.category', 'Category')}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat._id || cat.id} value={cat._id || cat.id}>
                    {cat.icon || '📁'} {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Date */}
          <TextField
            label={t('transactions.date', 'Date')}
            type="date"
            value={formData.date}
            onChange={handleChange('date')}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <IconButton onClick={onRetry} size="small" title={t('voice.tryAgain', 'Try Again')}>
          <ReplayIcon />
        </IconButton>
        <Button
          onClick={handleEditMore}
          startIcon={<EditIcon />}
          size="small"
        >
          {t('voice.editMore', 'Edit More')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<CheckIcon />}
          disabled={!isValid}
          size="small"
        >
          {t('voice.saveTransaction', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
